import type { Expression } from '~/prix/types/expression'
import { expressionEvery, iterateAndCheckIfSomeExpression } from '~/prix/utils/expressionUtils'

type OptionalExpression<ExpressionGeneric extends Expression = Expression> =
  | ExpressionGeneric
  | null
  | undefined

/**
 * Separa uma expressão de filtro em duas partes:
 * 1. os trechos que usam uma propriedade específica;
 * 2. o restante da expressão.
 *
 * No fluxo do polígono inteligente isso é usado para destacar o filtro
 * `agentPolygonCpf`, deixando os outros filtros livres para influenciar a
 * geração do polígono sem misturar a regra de seleção do agente.
 */
const combineExpressionsWithAnd = <ExpressionGeneric extends Expression>(
  ...expressions: OptionalExpression<ExpressionGeneric>[]
): ExpressionGeneric => {
  return expressionEvery(...expressions) as ExpressionGeneric
}

const flattenAnd = <ExpressionGeneric extends Expression>(
  expression: ExpressionGeneric,
): ExpressionGeneric[] => {
  if (expression.kind !== 'binary' || expression.operator !== 'AND') {
    return [expression]
  }

  return [
    ...flattenAnd(expression.left as ExpressionGeneric),
    ...flattenAnd(expression.right as ExpressionGeneric),
  ]
}

const hasExpressionProperty = <ExpressionGeneric extends Expression>(
  expression: OptionalExpression<ExpressionGeneric>,
  property: string,
): boolean => {
  if (!expression) {
    return false
  }

  return iterateAndCheckIfSomeExpression(
    expression,
    currentExpression =>
      currentExpression.kind === 'property' && currentExpression.property === property,
  )
}

export default function splitFilterByProperty<ExpressionGeneric extends Expression>(
  filter: ExpressionGeneric,
  property: string,
): {
  matchingFilter: ExpressionGeneric | null
  remainingFilter: ExpressionGeneric | null
} {
  const matchingParts: ExpressionGeneric[] = []
  const remainingParts: ExpressionGeneric[] = []

  for (const part of flattenAnd(filter)) {
    if (hasExpressionProperty(part, property)) {
      matchingParts.push(part)
      continue
    }

    remainingParts.push(part)
  }

  return {
    matchingFilter: matchingParts.length ? combineExpressionsWithAnd(...matchingParts) : null,
    remainingFilter: remainingParts.length ? combineExpressionsWithAnd(...remainingParts) : null,
  }
}
